/**
 * Cookie Utility Functions
 * Provides reliable cookie management with proper expiration and security
 */

export interface CookieOptions {
  expires?: number; // days from now
  maxAge?: number; // seconds
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
}

/**
 * Set a cookie with proper options
 * @param name Cookie name
 * @param value Cookie value (will be URI encoded)
 * @param options Cookie configuration
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {}
): void {
  if (typeof document === "undefined") {
    // Server-side, cannot set cookies this way
    return;
  }

  const {
    expires,
    maxAge,
    path = "/",
    domain,
    secure = process.env.NODE_ENV === "production",
    sameSite = "lax",
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  // Add path
  cookieString += `; path=${path}`;

  // Add expiration
  if (expires !== undefined) {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + expires);
    cookieString += `; expires=${expiryDate.toUTCString()}`;
  }

  // Add max-age
  if (maxAge !== undefined) {
    cookieString += `; max-age=${maxAge}`;
  }

  // Add domain
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Add secure flag
  if (secure) {
    cookieString += `; secure`;
  }

  // Add SameSite
  cookieString += `; SameSite=${sameSite}`;

  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 * @param name Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined") {
    // Server-side, cannot read cookies this way
    return null;
  }

  const nameEQ = encodeURIComponent(name) + "=";
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === " ") {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(
        cookie.substring(nameEQ.length, cookie.length)
      );
    }
  }

  return null;
}

/**
 * Remove a cookie
 * @param name Cookie name
 * @param path Cookie path (must match the path used when setting)
 * @param domain Cookie domain (must match the domain used when setting)
 */
export function removeCookie(
  name: string,
  path: string = "/",
  domain?: string
): void {
  if (typeof document === "undefined") {
    return;
  }

  let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  document.cookie = cookieString;
}

/**
 * Check if a cookie exists
 * @param name Cookie name
 * @returns True if cookie exists
 */
export function hasCookie(name: string): boolean {
  return getCookie(name) !== null;
}

/**
 * Get all cookies as an object
 * @returns Object with cookie names as keys and values as values
 */
export function getAllCookies(): Record<string, string> {
  if (typeof document === "undefined") {
    return {};
  }

  const cookies: Record<string, string> = {};
  const cookieArray = document.cookie.split(";");

  for (const cookie of cookieArray) {
    const [name, value] = cookie.split("=").map((c) => c.trim());
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  }

  return cookies;
}
